import { useEffect } from "react";
import { getCookiePreferences, type CookiePreferences } from "@/components/cookies/CookieConsent";

/**
 * Plausible Analytics — chargé uniquement si le consentement « performance » est accordé.
 *
 * - Le script est injecté à la volée après acceptation (jamais au chargement initial sans consentement).
 * - Si l'utilisateur révoque, le script est retiré et la file `plausible.q` purgée.
 * - Plausible est cookieless par design, mais on le conditionne quand même à la catégorie
 *   « performance » pour respecter strictement le choix de l'utilisateur (RGPD / CNIL).
 */

const SCRIPT_ID = "plausible-analytics";
const SCRIPT_SRC = "https://plausible.io/js/script.js";
const DATA_DOMAIN = "klikan.fr,klikan-fr.lovable.app";

declare global {
  interface Window {
    plausible?: ((event: string, options?: Record<string, unknown>) => void) & {
      q?: unknown[];
    };
  }
}

function inject() {
  if (typeof document === "undefined") return;
  if (document.getElementById(SCRIPT_ID)) return;
  const s = document.createElement("script");
  s.id = SCRIPT_ID;
  s.defer = true;
  s.src = SCRIPT_SRC;
  s.setAttribute("data-domain", DATA_DOMAIN);
  document.head.appendChild(s);

  // Stub conforme à la doc Plausible pour bufferiser d'éventuels events custom
  // émis avant que le script asynchrone ne soit chargé.
  if (!window.plausible) {
    const stub = function (...args: unknown[]) {
      (stub.q = stub.q || []).push(args);
    } as Window["plausible"] & { q: unknown[] };
    stub.q = [];
    window.plausible = stub;
  }
}

function remove() {
  if (typeof document === "undefined") return;
  const existing = document.getElementById(SCRIPT_ID);
  if (existing) existing.remove();
  if (window.plausible) {
    try {
      delete window.plausible;
    } catch {
      window.plausible = undefined;
    }
  }
}

function shouldLoad(prefs: CookiePreferences | null) {
  return !!prefs?.performance;
}

const PlausibleAnalytics = () => {
  useEffect(() => {
    const apply = () => {
      if (shouldLoad(getCookiePreferences())) inject();
      else remove();
    };

    apply();

    const onChange = () => apply();
    window.addEventListener("cookie-consent-changed", onChange);
    return () => {
      window.removeEventListener("cookie-consent-changed", onChange);
    };
  }, []);

  return null;
};

export default PlausibleAnalytics;
